/**
 * Custom text styles in the CI settings (spec 2026-10-15 §6): the list
 * designTokens.typography.customStyles, edited in place with Vue.set so the
 * Brand Kit sees every change. The emitter (ciTokens.js) checks every value
 * again; this file only keeps what the fields show in a valid shape.
 */
import Vue from 'vue';
import { CUSTOM_STYLE_ID_RE, CUSTOM_STYLES_MAX } from '@/editor-v2/utils/ciTokens';

export { CUSTOM_STYLES_MAX };

const UMLAUTS = {
  ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss',
};

/** The id a new style gets from its name: lower case, umlauts spelled out, [a-z0-9-], `-2` on a clash. */
export function styleIdFrom(name, takenIds) {
  const base = String(name).toLowerCase()
    .replace(/[äöüß]/g, (c) => UMLAUTS[c])
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 26)
    .replace(/-+$/, '') || 'textart';
  let id = base;
  for (let n = 2; takenIds.includes(id); n += 1) id = `${base}-${n}`;
  return CUSTOM_STYLE_ID_RE.test(id) ? id : null;
}

/** Adds a style named `name` at the end; null when the name is empty or the list is full. */
export function addCustomStyle(typo, name) {
  const clean = String(name || '').trim().slice(0, 40);
  if (!clean) return null;
  if (!Array.isArray(typo.customStyles)) Vue.set(typo, 'customStyles', []);
  const list = typo.customStyles;
  if (list.length >= CUSTOM_STYLES_MAX) return null;
  const id = styleIdFrom(clean, list.map((s) => s.id));
  if (!id) return null;
  const style = {
    id, name: clean, font: 'text', min: 1, max: 1,
  };
  list.push(style);
  return style;
}

/** Removes the style at `index`; an empty list goes away. */
export function removeCustomStyle(typo, index) {
  typo.customStyles.splice(index, 1);
  if (!typo.customStyles.length) Vue.delete(typo, 'customStyles');
}

/** Moves the style at `index` one place up (-1) or down (+1). */
export function moveCustomStyle(typo, index, delta) {
  const list = typo.customStyles;
  const to = index + delta;
  if (to < 0 || to >= list.length) return;
  list.splice(to, 0, list.splice(index, 1)[0]);
}

/** Sets one field; undefined, null or '' removes it (the text keeps its own value). */
export function setCustomStyleField(style, field, value) {
  if (value === undefined || value === null || value === '') Vue.delete(style, field);
  else Vue.set(style, field, value);
}

/** '#rrggbb' and opacity 0-100 of a stored colour (#rgb, #rrggbb, #rrggbbaa); null when none is set. */
export function colorParts(color) {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(String(color || ''));
  if (!m) return null;
  let hex = m[1].toLowerCase();
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  const alpha = hex.length === 8 ? Math.round((parseInt(hex.slice(6), 16) / 255) * 100) : 100;
  return { hex: `#${hex.slice(0, 6)}`, alpha };
}

/** The stored colour: #rrggbb at 100 %, else #rrggbbaa. */
export function colorFrom(hex, alpha) {
  const a = Math.min(100, Math.max(0, Math.round(Number(alpha))));
  if (a === 100) return hex;
  return `${hex}${Math.round((a / 100) * 255).toString(16).padStart(2, '0')}`;
}

const unquote = (n) => (typeof n === 'string' ? n.replace(/^'(.*)'$/, '$1') : n);

/** Inline style of a preview line: the style at its desktop size, at most `maxPx`. */
export function previewStyle(style, typo, maxPx) {
  let { font } = style;
  if (font === 'display') font = unquote(typo.displayFont);
  else if (font === 'text' || !font) font = unquote(typo.textFont);
  const out = {
    fontFamily: font && /^[^'"\\{};<>]+$/.test(font) ? `'${font}', sans-serif` : 'sans-serif',
    fontSize: `${Math.min(maxPx, Math.round((style.max || 1) * 16))}px`,
    lineHeight: '1.2',
  };
  if (style.weight) out.fontWeight = style.weight;
  if (typeof style.italic === 'boolean') out.fontStyle = style.italic ? 'italic' : 'normal';
  if (typeof style.uppercase === 'boolean') out.textTransform = style.uppercase ? 'uppercase' : 'none';
  if (typeof style.ls === 'number') out.letterSpacing = `${style.ls}em`;
  if (style.color) out.color = style.color;
  return out;
}
